import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import { buildUpiUri } from '../utils/upi';

interface Payment {
    id: string;
    type: string;
    amount: number;
    transactionId: string;
    date: string;
    status: string;
}

interface Booking {
    id: string;
    vehicleBrand: string;
    vehicleName: string;
    vehicleImage?: string;
    pickupDate: string;
    dropoffDate: string;
    pickupLocation: string;
    dropoffLocation: string;
    kms: string;
    amount: number;
    transactionId: string;
    payments?: Payment[];
    status: string;
    dateBooked: string;
}

interface Complaint {
    id: string;
    bookingId: string;
    category: string;
    description: string;
    status: string;
    dateFiled: string;
}

function MyAccount() {
    const navigate = useNavigate();
    const [user, setUser] = useState<{
        name: string;
        email: string;
        phone: string;
        aadharNo?: string;
        dlNo?: string;
        aadharImage?: string;
        dlImage?: string;
    } | null>(() => {
        const saved = localStorage.getItem('currentUser');
        return saved ? JSON.parse(saved) : null;
    });

    // Sidebar selected tab option
    const [activeTab, setActiveTab] = useState<string>('profile');

    // Registered Users list
    const [registeredUsers, setRegisteredUsers] = useState<any[]>(() => {
        const saved = localStorage.getItem('registeredUsers');
        const list = saved ? JSON.parse(saved) : [];
        const current = localStorage.getItem('currentUser');
        if (current) {
            const currentUserData = JSON.parse(current);
            const exists = list.some((u: any) => u.email.toLowerCase() === currentUserData.email.toLowerCase());
            if (!exists) {
                const seededUser = {
                    name: currentUserData.name,
                    email: currentUserData.email,
                    phone: currentUserData.phone,
                    password: 'password123'
                };
                list.push(seededUser);
                localStorage.setItem('registeredUsers', JSON.stringify(list));
            }
        }
        return list;
    });

    // Verification/Login Fallback form state
    const [loginForm, setLoginForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    const [authStep, setAuthStep] = useState<'email' | 'login' | 'register'>('email');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [authError, setAuthError] = useState('');

    // Local Storage lists
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [complaints, setComplaints] = useState<Complaint[]>([]);

    // Profile details form state
    const [profileForm, setProfileForm] = useState({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        aadharNo: user?.aadharNo || '',
        dlNo: user?.dlNo || '',
        aadharImage: user?.aadharImage || '',
        dlImage: user?.dlImage || ''
    });
    const [profileSuccessMessage, setProfileSuccessMessage] = useState<string>('');

    // Create a Complaint form state
    const [selectedBookingForComplaint, setSelectedBookingForComplaint] = useState<string>('');
    const [complaintCategory, setComplaintCategory] = useState<string>('Vehicle issue');
    const [complaintDesc, setComplaintDesc] = useState<string>('');
    const [complaintSuccessMessage, setComplaintSuccessMessage] = useState<string>('');

    // Help query form state
    const [helpSubject, setHelpSubject] = useState<string>('General Enquiry');
    const [helpMessage, setHelpMessage] = useState<string>('');
    const [helpSuccessMessage, setHelpSuccessMessage] = useState<string>('');

    // Multiple sub-payments modal states
    const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
    const [paymentModalForm, setPaymentModalForm] = useState({
        bookingId: '',
        vehicleDetails: '',
        type: 'Remaining Balance',
        amount: '',
        transactionId: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        if (user) {
            const savedBookingsStr = localStorage.getItem('myBookings');
            const savedBookings = savedBookingsStr ? JSON.parse(savedBookingsStr) : [];
            setBookings(savedBookings);

            const savedComplaints = localStorage.getItem('myComplaints');
            if (savedComplaints) {
                setComplaints(JSON.parse(savedComplaints));
            }

            // Sync form state if user session details change
            setProfileForm({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                aadharNo: user.aadharNo || '',
                dlNo: user.dlNo || '',
                aadharImage: user.aadharImage || '',
                dlImage: user.dlImage || ''
            });
        }
    }, [user]);

    const handleEmailContinue = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        if (!loginForm.email) return;

        const emailLower = loginForm.email.toLowerCase().trim();
        const exists = registeredUsers.some((u: any) => u.email.toLowerCase() === emailLower);
        if (exists) {
            setAuthStep('login');
        } else {
            setAuthStep('register');
        }
    };

    const handleLoginPasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        const emailLower = loginForm.email.toLowerCase().trim();
        const foundUser = registeredUsers.find((u: any) => u.email.toLowerCase() === emailLower);
        if (foundUser) {
            if (foundUser.password === loginForm.password) {
                setUser(foundUser);
                localStorage.setItem('currentUser', JSON.stringify(foundUser));
                window.dispatchEvent(new Event('auth-change'));
            } else {
                setAuthError('Incorrect password. Please try again.');
            }
        } else {
            setAuthStep('email');
        }
    };

    const handleRegisterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');

        if (!loginForm.name || !loginForm.email || !loginForm.phone || !loginForm.password) {
            setAuthError('All fields are required.');
            return;
        }

        if (loginForm.password !== loginForm.confirmPassword) {
            setAuthError('Passwords do not match.');
            return;
        }

        if (!agreeTerms) {
            setAuthError('You must agree to the Terms & Conditions.');
            return;
        }

        const emailLower = loginForm.email.toLowerCase().trim();
        const exists = registeredUsers.some((u: any) => u.email.toLowerCase() === emailLower);
        if (exists) {
            setAuthError('User with this email already registered.');
            return;
        }

        const newUser = {
            name: loginForm.name,
            email: loginForm.email,
            phone: loginForm.phone,
            password: loginForm.password
        };

        const updatedUsers = [...registeredUsers, newUser];
        setRegisteredUsers(updatedUsers);
        localStorage.setItem('registeredUsers', JSON.stringify(updatedUsers));

        setUser(newUser);
        localStorage.setItem('currentUser', JSON.stringify(newUser));
        window.dispatchEvent(new Event('auth-change'));
    };

    const handleBackToEmail = () => {
        setAuthStep('email');
        setLoginForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
        setAuthError('');
    };

    const handleLogout = () => {
        localStorage.removeItem('currentUser');
        setUser(null);
        window.dispatchEvent(new Event('auth-change'));
        navigate('/');
    };

    const handleProfileSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (user) {
            const updatedUser = {
                ...user,
                name: profileForm.name,
                email: profileForm.email,
                phone: profileForm.phone,
                aadharNo: profileForm.aadharNo,
                dlNo: profileForm.dlNo,
                aadharImage: profileForm.aadharImage,
                dlImage: profileForm.dlImage
            };
            setUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            window.dispatchEvent(new Event('auth-change'));
            setProfileSuccessMessage('Profile and identity document records updated successfully!');
            setTimeout(() => setProfileSuccessMessage(''), 5000);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'aadharImage' | 'dlImage') => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert("File size is too large. Please select an image under 2MB.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileForm(prev => ({
                    ...prev,
                    [field]: reader.result as string
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleComplaintSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (complaintDesc) {
            const newComplaint: Complaint = {
                id: `APG-COMP-${Math.floor(100000 + Math.random() * 900000)}`,
                bookingId: selectedBookingForComplaint || 'N/A',
                category: complaintCategory,
                description: complaintDesc,
                status: 'Under Review',
                dateFiled: new Date().toISOString()
            };

            const updated = [newComplaint, ...complaints];
            setComplaints(updated);
            localStorage.setItem('myComplaints', JSON.stringify(updated));
            setComplaintDesc('');
            setComplaintSuccessMessage(`Complaint registered successfully. Ref ID: ${newComplaint.id}`);
            setTimeout(() => setComplaintSuccessMessage(''), 5000);
        }
    };

    const handleHelpSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (helpMessage) {
            const ticketId = `APG-TKT-${Math.floor(100000 + Math.random() * 900000)}`;
            setHelpMessage('');
            setHelpSuccessMessage(`Support request submitted successfully. Ref ID: ${ticketId}. Our team will contact you shortly.`);
            setTimeout(() => setHelpSuccessMessage(''), 5000);
        }
    };

    const handleRefundRequest = (bookingId: string) => {
        const updatedBookings = bookings.map(b => {
            if (b.id === bookingId) {
                return { ...b, status: 'Refund Requested' };
            }
            return b;
        });
        setBookings(updatedBookings);
        localStorage.setItem('myBookings', JSON.stringify(updatedBookings));
    };

    // Trigger downloading full structured booking receipt details as a neatly formed PDF format
    const handleDownloadReceipt = (booking: Booking) => {
        const paymentsList = booking.payments || [
            {
                id: 'PMT-LEGACY',
                type: 'Booking Advance',
                amount: booking.amount,
                transactionId: booking.transactionId,
                date: booking.dateBooked,
                status: booking.status
            }
        ];
        const totalPaid = paymentsList.reduce((sum, p) => sum + p.amount, 0);

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        // 1. Top Solid Dark Header Bar
        doc.setFillColor(15, 23, 42); // slate-800
        doc.rect(0, 0, 210, 40, "F");

        // Corporate Branding Text
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(255, 255, 255);
        doc.text("AAPLI GAADI RENTALS", 20, 20);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("Premium Self-Drive & Chauffeur Services", 20, 26);
        doc.text("Email: crewtechsolution@gmail.com | Call: +91 7208388380", 20, 31);

        // Receipt Metadata inside header block
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("BOOKING RECEIPT", 140, 18);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Ref: REC-${booking.id}`, 140, 24);
        doc.text(`Issued: ${moment(booking.dateBooked).format('ll')}`, 140, 29);

        // Reset text configuration for body
        doc.setTextColor(30, 41, 59); // slate-700

        // 2. Customer Profile details section
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("CUSTOMER DETAILS", 20, 55);
        doc.setLineWidth(0.3);
        doc.setDrawColor(226, 232, 240);
        doc.line(20, 57, 190, 57);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Name: ${user?.name || ''}`, 20, 63);
        doc.text(`Phone: ${user?.phone || ''}`, 20, 68);
        doc.text(`Email: ${user?.email || ''}`, 20, 73);

        // 3. Rental logistics details section
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("RENTAL & VEHICLE DETAILS", 20, 85);
        doc.line(20, 87, 190, 87);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Vehicle: ${booking.vehicleBrand} ${booking.vehicleName}`, 20, 93);
        doc.text(`KM Package: ${booking.kms}`, 20, 98);
        doc.text(`Pick-up Hub Location: ${booking.pickupLocation}`, 20, 103);
        doc.text(`Pick-up Schedule: ${moment(booking.pickupDate).format('LLLL')}`, 20, 108);
        doc.text(`Drop-off Location: ${booking.dropoffLocation}`, 20, 113);
        doc.text(`Drop-off Schedule: ${moment(booking.dropoffDate).format('LLLL')}`, 20, 118);

        // 4. Payment ledger transactions logs table
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("PAYMENT TRANSACTION LEDGER", 20, 132);
        doc.line(20, 134, 190, 134);

        // Table headers row background
        doc.setFillColor(248, 250, 252); // light slate background
        doc.rect(20, 138, 170, 8, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Payment ID", 22, 143);
        doc.text("Type", 52, 143);
        doc.text("Transaction Ref", 92, 143);
        doc.text("Date", 137, 143);
        doc.text("Amount", 170, 143);

        // Populate table entries
        let currentY = 151;
        doc.setFont("helvetica", "normal");
        paymentsList.forEach((p, idx) => {
            // Alternating rows shading
            if (idx % 2 === 1) {
                doc.setFillColor(250, 250, 250);
                doc.rect(20, currentY - 4, 170, 6, "F");
            }
            doc.text(p.id, 22, currentY);
            doc.text(p.type, 52, currentY);
            doc.text(p.transactionId, 92, currentY);
            doc.text(moment(p.date).format('ll'), 137, currentY);
            doc.text(`Rs. ${p.amount}/-`, 170, currentY);
            currentY += 6;
        });

        // Bottom border line for payments ledger table
        doc.line(20, currentY - 2, 190, currentY - 2);

        // 5. Total Billed, Total Paid summaries
        currentY += 5;
        doc.setFont("helvetica", "bold");
        doc.text(`Total Billed: Rs. ${booking.amount}/-`, 130, currentY);
        currentY += 5;
        doc.text(`Total Paid: Rs. ${totalPaid}/-`, 130, currentY);
        currentY += 5;
        doc.setFontSize(10);
        doc.setTextColor(1, 210, 142); // primary green color theme
        doc.text(`Status: ${booking.status}`, 130, currentY);

        // 6. Corporate legal footer info block
        doc.setTextColor(100, 116, 139); // slate-500
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("Thank you for choosing Aapli Gaadi. Wish you a safe and pleasant journey!", 105, 275, { align: "center" });
        doc.text("For roadside help or enquiries call +91 7208388380 or email crewtechsolution@gmail.com", 105, 280, { align: "center" });

        doc.save(`AapliGaadi_Receipt_${booking.id}.pdf`);
    };

    const openNewPaymentModal = (booking: Booking) => {
        setPaymentModalForm({
            bookingId: booking.id,
            vehicleDetails: `${booking.vehicleBrand} ${booking.vehicleName}`,
            type: 'Remaining Balance',
            amount: '',
            transactionId: ''
        });
        setShowPaymentModal(true);
    };

    const handleNewPaymentSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (paymentModalForm.transactionId && paymentModalForm.amount) {
            const updatedBookings = bookings.map(b => {
                if (b.id === paymentModalForm.bookingId) {
                    const newPayment: Payment = {
                        id: `PMT-${Math.floor(100000 + Math.random() * 900000)}`,
                        type: paymentModalForm.type,
                        amount: Number(paymentModalForm.amount),
                        transactionId: paymentModalForm.transactionId,
                        date: new Date().toISOString(),
                        status: 'Pending Verification'
                    };
                    const currentPayments = b.payments || [
                        {
                            id: 'PMT-LEGACY',
                            type: 'Booking Advance',
                            amount: b.amount,
                            transactionId: b.transactionId,
                            date: b.dateBooked,
                            status: b.status
                        }
                    ];
                    return {
                        ...b,
                        payments: [...currentPayments, newPayment]
                    };
                }
                return b;
            });

            setBookings(updatedBookings);
            localStorage.setItem('myBookings', JSON.stringify(updatedBookings));

            const message = `Hello Aapli Gaadi, I have initiated a new payment for my Booking ID ${paymentModalForm.bookingId} (${paymentModalForm.vehicleDetails}).\n\nPayment Details:\n- Type: ${paymentModalForm.type}\n- Amount: ₹${paymentModalForm.amount}/-\n- UPI Transaction ID: ${paymentModalForm.transactionId}\n\nPlease verify and update my booking ledger.`;
            const whatsappLink = `https://wa.me/917208388380?text=${encodeURIComponent(message)}`;
            window.open(whatsappLink, '_blank', 'noopener,noreferrer');

            setShowPaymentModal(false);
        }
    };

    // Helper to generate UPI URI for dynamic QR generation
    const getSubPaymentUpiUri = () => {
        const amt = paymentModalForm.amount || '0';
        return buildUpiUri(amt, 'Aapli Gaadi Rental Payment');
    };

    // Sleek Left Menu button styling definitions
    const getSidebarButtonStyle = (tabName: string) => {
        const isActive = activeTab === tabName;
        return {
            background: isActive ? 'rgba(1, 210, 142, 0.08)' : 'transparent',
            color: isActive ? '#01d28e' : '#475569',
            borderLeft: isActive ? '4px solid #01d28e' : '4px solid transparent',
            borderRadius: '0 8px 8px 0',
            padding: '14px 24px',
            fontSize: '14px',
            fontWeight: 600,
            textAlign: 'left' as const,
            borderTop: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            outline: 'none',
            transition: 'all 0.2s ease-in-out',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
        };
    };

    // Hero section to enable high-contrast header navigation display
    const myAccountHero = (
        <div className="hero-wrap ftco-degree-bg" style={{ backgroundImage: `url('/image/bg_1.jpg')`, height: '280px', backgroundPosition: 'center', backgroundSize: 'cover', position: 'relative' }}>
            <div className="overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000', opacity: 0.45 }}></div>
            <div className="container" style={{ position: 'relative', height: '100%' }}>
                <div className="row no-gutters slider-text justify-content-center align-items-center" style={{ height: '100%' }}>
                    <div className="col-md-9 text-center text-white" style={{ paddingTop: '80px' }}>
                        <p className="breadcrumbs mb-2" style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                            <Link to="/" style={{ color: '#fff' }}>Home</Link> <span className="mx-2">&gt;</span>
                            <span style={{ color: 'var(--primary-color)' }}>My Account</span>
                        </p>
                        <h1 className="mb-0 text-white font-weight-bold" style={{ fontSize: '32px' }}>My Account</h1>
                    </div>
                </div>
            </div>
        </div>
    );

    // If unauthenticated, show high-end Login/Registration panel with Hero header overlay
    if (!user) {
        return (
            <>
                {myAccountHero}
                <div className="container-fluid bg-light py-5" style={{ minHeight: '50vh' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-md-6 col-lg-5">
                                <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '15px' }}>
                                    <div className="text-center mb-4">
                                        <h2 className="font-weight-bold text-dark mb-1" style={{ fontSize: '24px' }}>
                                            {authStep === 'email' && 'Log In / Register'}
                                            {authStep === 'login' && 'Log In'}
                                            {authStep === 'register' && 'Create Account'}
                                        </h2>
                                        <p className="text-muted small mb-0">
                                            {authStep === 'email' && 'Enter your email address to access your personal dashboard.'}
                                            {authStep === 'login' && 'Enter your password to log in.'}
                                            {authStep === 'register' && 'Complete your details to set up your account.'}
                                        </p>
                                    </div>

                                    {authError && (
                                        <div className="alert alert-danger py-2 px-3 small rounded mb-3 text-center" style={{ fontSize: '13px' }}>
                                            {authError}
                                        </div>
                                    )}

                                    {authStep === 'email' && (
                                        <form onSubmit={handleEmailContinue}>
                                            <div className="form-group mb-4">
                                                <label className="text-dark font-weight-bold small mb-1">Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    required
                                                    value={loginForm.email}
                                                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                                    placeholder="name@example.com"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Continue
                                            </button>
                                        </form>
                                    )}

                                    {authStep === 'login' && (
                                        <form onSubmit={handleLoginPasswordSubmit}>
                                            <div className="form-group mb-3">
                                                <label className="text-muted small mb-1">Logging in as:</label>
                                                <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light mb-3" style={{ border: '1px solid #e2e8f0' }}>
                                                    <span className="text-dark small font-weight-bold" style={{ wordBreak: 'break-all' }}>{loginForm.email}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 small text-primary"
                                                        onClick={handleBackToEmail}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="form-group mb-4">
                                                <label className="text-dark font-weight-bold small mb-1">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={loginForm.password}
                                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                                    placeholder="Enter your password"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Log In
                                            </button>
                                        </form>
                                    )}

                                    {authStep === 'register' && (
                                        <form onSubmit={handleRegisterSubmit}>
                                            <div className="form-group mb-3">
                                                <label className="text-muted small mb-1">Registering email:</label>
                                                <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light mb-3" style={{ border: '1px solid #e2e8f0' }}>
                                                    <span className="text-dark small font-weight-bold" style={{ wordBreak: 'break-all' }}>{loginForm.email}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 small text-primary"
                                                        onClick={handleBackToEmail}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Full Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    required
                                                    value={loginForm.name}
                                                    onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                                                    placeholder="Enter your full name"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    required
                                                    pattern="[0-9]{10}"
                                                    title="Please enter a valid 10-digit mobile number"
                                                    value={loginForm.phone}
                                                    onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                                                    placeholder="10-digit mobile number"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Create Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={loginForm.password}
                                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                                    placeholder="Choose a password"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <div className="form-group mb-3">
                                                <label className="text-dark font-weight-bold small mb-1">Confirm Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    required
                                                    value={loginForm.confirmPassword}
                                                    onChange={(e) => setLoginForm({ ...loginForm, confirmPassword: e.target.value })}
                                                    placeholder="Confirm your password"
                                                    style={{ height: '42px', borderRadius: '8px' }}
                                                />
                                            </div>
                                            <div className="form-group mb-4 form-check d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0 mr-2"
                                                    id="agreeTermsCheck"
                                                    required
                                                    checked={agreeTerms}
                                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                />
                                                <label className="form-check-label text-dark small mb-0" htmlFor="agreeTermsCheck" style={{ cursor: 'pointer', userSelect: 'none' }}>
                                                    I agree to the{' '}
                                                    <Link to="/terms-and-conditions" target="_blank" className="font-weight-bold text-primary" style={{ textDecoration: 'underline' }}>
                                                        Terms & Conditions
                                                    </Link>
                                                </label>
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block py-2 font-weight-bold"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Register & Log In
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {myAccountHero}
            <div className="container-fluid bg-light py-5" style={{ minHeight: '70vh' }}>
                <div className="container">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm p-4 d-flex flex-row justify-content-between align-items-center flex-wrap" style={{ borderRadius: '15px' }}>
                                <div>
                                    <span className="text-muted small">Welcome Back,</span>
                                    <h2 className="h3 font-weight-bold text-dark mb-0">{user.name}</h2>
                                    <span className="text-muted small">{user.email} | {user.phone}</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger px-4 py-2 mt-3 mt-md-0"
                                    onClick={handleLogout}
                                    style={{ borderRadius: '30px', fontWeight: 'bold' }}
                                >
                                    Log Out
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Left Sidebar Menu */}
                        <div className="col-md-4 col-lg-3 mb-4">
                            <div className="card border-0 shadow-sm py-4 px-0" style={{ borderRadius: '15px', position: 'sticky', top: '100px', backgroundColor: '#fff', overflow: 'hidden' }}>
                                <h3 className="h6 font-weight-bold text-muted uppercase tracking-wider mb-3 px-4">Account Menu</h3>
                                <div className="nav flex-column" style={{ gap: '4px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('profile')}
                                        style={getSidebarButtonStyle('profile')}
                                    >
                                        <span style={{ fontSize: '16px' }}>👤</span> My Profile
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('bookings')}
                                        style={getSidebarButtonStyle('bookings')}
                                    >
                                        <span style={{ fontSize: '16px' }}>🚗</span> My Bookings
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('help')}
                                        style={getSidebarButtonStyle('help')}
                                    >
                                        <span style={{ fontSize: '16px' }}>❔</span> Help & FAQ
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('complaints')}
                                        style={getSidebarButtonStyle('complaints')}
                                    >
                                        <span style={{ fontSize: '16px' }}>⚠️</span> File a Complaint
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('refunds')}
                                        style={getSidebarButtonStyle('refunds')}
                                    >
                                        <span style={{ fontSize: '16px' }}>💸</span> Refunds Timeline
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Side Content Section */}
                        <div className="col-md-8 col-lg-9">
                            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '15px', minHeight: '50vh' }}>
                                {activeTab === 'profile' && (
                                    <div>
                                        <h2 className="h4 font-weight-bold text-dark border-bottom pb-2 mb-4">My Profile</h2>
                                        {profileSuccessMessage && (
                                            <div className="alert alert-success py-2 px-3 small mb-4" style={{ borderRadius: '8px' }}>
                                                ✨ {profileSuccessMessage}
                                            </div>
                                        )}
                                        <form onSubmit={handleProfileSubmit}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="text-dark font-weight-bold small mb-1">Full Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        required
                                                        value={profileForm.name}
                                                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                                                        style={{ height: '42px', borderRadius: '8px' }}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="text-dark font-weight-bold small mb-1">Email Address</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        required
                                                        value={profileForm.email}
                                                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                                                        style={{ height: '42px', borderRadius: '8px' }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row mb-4">
                                                <div className="col-md-6 mb-3">
                                                    <label className="text-dark font-weight-bold small mb-1">Phone Number</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        required
                                                        pattern="[0-9]{10}"
                                                        title="Please enter a valid 10-digit mobile number"
                                                        value={profileForm.phone}
                                                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                                                        style={{ height: '42px', borderRadius: '8px' }}
                                                    />
                                                </div>
                                            </div>

                                            <h3 className="h6 font-weight-bold text-dark border-bottom pb-2 mt-4 mb-3">Identity & Verification Documents</h3>

                                            <div className="row">
                                                {/* Aadhar Card Section */}
                                                <div className="col-md-6 mb-4">
                                                    <div className="p-3 border rounded bg-white h-100">
                                                        <label className="text-dark font-weight-bold small mb-1">Aadhar Card Number</label>
                                                        <input
                                                            type="text"
                                                            className="form-control mb-3"
                                                            placeholder="12-digit Aadhar number"
                                                            pattern="[0-9]{12}"
                                                            title="Please enter a valid 12-digit Aadhar number"
                                                            value={profileForm.aadharNo}
                                                            onChange={(e) => setProfileForm({ ...profileForm, aadharNo: e.target.value })}
                                                            style={{ height: '40px', borderRadius: '6px' }}
                                                        />
                                                        <label className="text-dark font-weight-bold small d-block mb-1">Aadhar Card Image</label>
                                                        <div className="custom-file mb-3" style={{ height: '36px' }}>
                                                            <input
                                                                type="file"
                                                                className="custom-file-input"
                                                                accept="image/*"
                                                                id="aadharFile"
                                                                onChange={(e) => handleFileChange(e, 'aadharImage')}
                                                            />
                                                            <label className="custom-file-label text-muted small" htmlFor="aadharFile" style={{ height: '36px', overflow: 'hidden' }}>
                                                                Choose Aadhar card image...
                                                            </label>
                                                        </div>
                                                        {profileForm.aadharImage && (
                                                            <div className="mt-3 text-center border p-2 rounded bg-light" style={{ position: 'relative' }}>
                                                                <img
                                                                    src={profileForm.aadharImage}
                                                                    alt="Aadhar Preview"
                                                                    style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '4px' }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger position-absolute"
                                                                    style={{ top: '5px', right: '5px', borderRadius: '50%', padding: '2px 8px', fontSize: '10px' }}
                                                                    onClick={() => setProfileForm({ ...profileForm, aadharImage: '' })}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Driving License Section */}
                                                <div className="col-md-6 mb-4">
                                                    <div className="p-3 border rounded bg-white h-100">
                                                        <label className="text-dark font-weight-bold small mb-1">Driving License Number</label>
                                                        <input
                                                            type="text"
                                                            className="form-control mb-3"
                                                            placeholder="DL Number (e.g. MH-14-20150001234)"
                                                            value={profileForm.dlNo}
                                                            onChange={(e) => setProfileForm({ ...profileForm, dlNo: e.target.value })}
                                                            style={{ height: '40px', borderRadius: '6px' }}
                                                        />
                                                        <label className="text-dark font-weight-bold small d-block mb-1">Driving License Image</label>
                                                        <div className="custom-file mb-3" style={{ height: '36px' }}>
                                                            <input
                                                                type="file"
                                                                className="custom-file-input"
                                                                accept="image/*"
                                                                id="dlFile"
                                                                onChange={(e) => handleFileChange(e, 'dlImage')}
                                                            />
                                                            <label className="custom-file-label text-muted small" htmlFor="dlFile" style={{ height: '36px', overflow: 'hidden' }}>
                                                                Choose Driving License image...
                                                            </label>
                                                        </div>
                                                        {profileForm.dlImage && (
                                                            <div className="mt-3 text-center border p-2 rounded bg-light" style={{ position: 'relative' }}>
                                                                <img
                                                                    src={profileForm.dlImage}
                                                                    alt="Driving License Preview"
                                                                    style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '4px' }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger position-absolute"
                                                                    style={{ top: '5px', right: '5px', borderRadius: '50%', padding: '2px 8px', fontSize: '10px' }}
                                                                    onClick={() => setProfileForm({ ...profileForm, dlImage: '' })}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn btn-success px-5 py-2 font-weight-bold mt-3"
                                                style={{ borderRadius: '30px', fontSize: '15px' }}
                                            >
                                                Save Profile Details
                                            </button>
                                        </form>
                                    </div>
                                )}

                                {activeTab === 'bookings' && (
                                    <div>
                                        <h2 className="h4 font-weight-bold text-dark border-bottom pb-2 mb-4">My Bookings</h2>
                                        {bookings.length === 0 ? (
                                            <div className="text-center py-5">
                                                <div style={{ fontSize: '40px' }}>🚗</div>
                                                <h4 className="font-weight-bold text-dark mt-3 mb-2">No Bookings Found</h4>
                                                <p className="text-muted small mb-4">You have not reserved any self-drive cars, bikes, or tours yet.</p>
                                                <Link to="/" className="btn btn-primary px-4 py-2" style={{ borderRadius: '30px' }}>
                                                    Book Now
                                                </Link>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                {bookings.map((booking) => {
                                                    const paymentsList = booking.payments || [
                                                        {
                                                            id: 'PMT-LEGACY',
                                                            type: 'Booking Advance',
                                                            amount: booking.amount,
                                                            transactionId: booking.transactionId,
                                                            date: booking.dateBooked,
                                                            status: booking.status
                                                        }
                                                    ];
                                                    const totalPaid = paymentsList.reduce((sum, p) => sum + p.amount, 0);

                                                    return (
                                                        <div className="col-12 mb-4" key={booking.id}>
                                                            <div className="card p-3 border" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                                                                <div className="d-flex justify-content-between align-items-start flex-wrap mb-3" style={{ gap: '15px' }}>
                                                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '15px' }}>
                                                                        {/* Stored product selection thumbnail preview */}
                                                                        <div className="rounded border bg-light p-1" style={{ width: '120px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                                            <img
                                                                                src={booking.vehicleImage ? `${process.env.PUBLIC_URL}/${booking.vehicleImage}` : `${process.env.PUBLIC_URL}/image/car-1.jpg`}
                                                                                alt={`${booking.vehicleBrand} ${booking.vehicleName}`}
                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                onError={(e) => {
                                                                                    (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/image/car-1.jpg`;
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <span className="badge badge-warning text-dark mb-1 px-3 py-1" style={{ borderRadius: '20px', fontSize: '11px' }}>
                                                                                {booking.status}
                                                                            </span>
                                                                            <h4 className="font-weight-bold text-dark mb-1 h5">{booking.vehicleBrand} {booking.vehicleName}</h4>
                                                                            <span className="text-muted small d-block">Booking ID: {booking.id} | Booked on: {moment(booking.dateBooked).format('LL')}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="text-muted small d-block">Total Billed</span>
                                                                        <strong className="text-primary d-block h5 font-weight-bold">₹{booking.amount}/-</strong>
                                                                        <span className="text-muted small">Total Paid: ₹{totalPaid}/-</span>
                                                                    </div>
                                                                </div>

                                                                <hr className="my-2" />
                                                                <div className="row small text-muted mb-2">
                                                                    <div className="col-sm-6 mb-2 mb-sm-0">
                                                                        <strong>Pick-up Point:</strong> {booking.pickupLocation}<br />
                                                                        <strong>Date & Time:</strong> {moment(booking.pickupDate).format('LLLL')}
                                                                    </div>
                                                                    <div className="col-sm-6">
                                                                        <strong>Drop-off Point:</strong> {booking.dropoffLocation}<br />
                                                                        <strong>Date & Time:</strong> {moment(booking.dropoffDate).format('LLLL')}
                                                                    </div>
                                                                </div>

                                                                {/* Itemized dynamic payment history table */}
                                                                <div className="mt-3 bg-light p-3 rounded" style={{ border: '1px solid #e2e8f0' }}>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <h5 className="h6 font-weight-bold text-dark mb-0">💰 Payment Ledger</h5>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-primary py-1 px-3"
                                                                            style={{ borderRadius: '15px', fontSize: '11px', fontWeight: 'bold' }}
                                                                            onClick={() => openNewPaymentModal(booking)}
                                                                        >
                                                                            ➕ Make New Payment
                                                                        </button>
                                                                    </div>
                                                                    <div className="table-responsive">
                                                                        <table className="table table-sm table-borderless mb-0 small text-muted" style={{ fontSize: '11px' }}>
                                                                            <thead>
                                                                                <tr className="text-dark border-bottom" style={{ fontWeight: 'bold' }}>
                                                                                    <th>Payment ID</th>
                                                                                    <th>Type</th>
                                                                                    <th>Transaction Ref</th>
                                                                                    <th>Date</th>
                                                                                    <th className="text-right">Amount</th>
                                                                                    <th className="text-right">Status</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {paymentsList.map((p) => (
                                                                                    <tr key={p.id} className="border-bottom">
                                                                                        <td>{p.id}</td>
                                                                                        <td><strong>{p.type}</strong></td>
                                                                                        <td><code>{p.transactionId}</code></td>
                                                                                        <td>{moment(p.date).format('ll')}</td>
                                                                                        <td className="text-right font-weight-bold text-dark">₹{p.amount}/-</td>
                                                                                        <td className="text-right">
                                                                                            <span className={`badge px-2 py-1 ${p.status === 'Verified' ? 'badge-success' : 'badge-warning text-dark'}`} style={{ borderRadius: '10px' }}>
                                                                                                {p.status}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>

                                                                <div className="d-flex justify-content-between align-items-center flex-wrap mt-3 small text-muted">
                                                                    <span><strong>Package:</strong> {booking.kms}</span>
                                                                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary px-3"
                                                                            style={{ borderRadius: '20px', fontWeight: 'bold' }}
                                                                            onClick={() => handleDownloadReceipt(booking)}
                                                                        >
                                                                            📄 Download Receipt
                                                                        </button>
                                                                        {booking.status === 'Pending Verification' && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger px-3"
                                                                                style={{ borderRadius: '20px', fontWeight: 'bold' }}
                                                                                onClick={() => handleRefundRequest(booking.id)}
                                                                            >
                                                                                Cancel & Refund
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'help' && (
                                    <div>
                                        <h2 className="h4 font-weight-bold text-dark border-bottom pb-2 mb-4">Help & FAQ Support</h2>

                                        <div className="mb-5">
                                            <h3 className="h6 font-weight-bold text-dark mb-3">Frequently Asked Questions</h3>
                                            <div className="accordion" id="helpFaq">
                                                <div className="card mb-2 border rounded">
                                                    <div className="card-header bg-white py-2 border-0">
                                                        <strong className="text-dark small d-block">1. How do I get booking confirmation?</strong>
                                                    </div>
                                                    <div className="card-body pt-0 text-muted small">
                                                        Once you scan the UPI QR code and provide the 12-digit transaction ID, click the confirm button to open WhatsApp. Our support agents verify the transaction instantly and issue a confirmed booking slip within 15 minutes.
                                                    </div>
                                                </div>
                                                <div className="card mb-2 border rounded">
                                                    <div className="card-header bg-white py-2 border-0">
                                                        <strong className="text-dark small d-block">2. What documents must be submitted?</strong>
                                                    </div>
                                                    <div className="card-body pt-0 text-muted small">
                                                        For Self-Drive rentals, you must provide a valid Driving License (DL) and an Aadhar Card or Passport matching the driver's profile during vehicle delivery.
                                                    </div>
                                                </div>
                                                <div className="card mb-2 border rounded">
                                                    <div className="card-header bg-white py-2 border-0">
                                                        <strong className="text-dark small d-block">3. Is security deposit refundable?</strong>
                                                    </div>
                                                    <div className="card-body pt-0 text-muted small">
                                                        Yes. The security deposit (if applicable for self-drive) is 100% refundable and is processed back to your original source within 24 to 48 hours of vehicle check-in.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-5 mb-4 mb-md-0">
                                                <div className="p-3 border rounded h-100 bg-white" style={{ borderStyle: 'dashed' }}>
                                                    <h4 className="h6 font-weight-bold text-dark mb-3">Direct Support Contacts</h4>
                                                    <p className="small text-muted mb-2">📞 <strong>Phone:</strong> +91 7208388380</p>
                                                    <p className="small text-muted mb-2">✉️ <strong>Email:</strong> crewtechsolution@gmail.com</p>
                                                    <p className="small text-muted mb-0">🕒 <strong>Hours:</strong> 24/7 Roadside Assistance</p>
                                                </div>
                                            </div>
                                            <div className="col-md-7">
                                                <div className="p-3 border rounded bg-white">
                                                    <h4 className="h6 font-weight-bold text-dark mb-3">Submit a Support Request</h4>
                                                    {helpSuccessMessage && (
                                                        <div className="alert alert-success small py-2 mb-3">{helpSuccessMessage}</div>
                                                    )}
                                                    <form onSubmit={handleHelpSubmit}>
                                                        <div className="form-group mb-2">
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={helpSubject}
                                                                onChange={(e) => setHelpSubject(e.target.value)}
                                                            >
                                                                <option>General Enquiry</option>
                                                                <option>Billing Issue</option>
                                                                <option>Vehicle Technical Problem</option>
                                                                <option>Other</option>
                                                            </select>
                                                        </div>
                                                        <div className="form-group mb-3">
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                required
                                                                rows={3}
                                                                value={helpMessage}
                                                                onChange={(e) => setHelpMessage(e.target.value)}
                                                                placeholder="Describe your query..."
                                                            />
                                                        </div>
                                                        <button type="submit" className="btn btn-sm btn-primary px-3" style={{ borderRadius: '20px' }}>
                                                            Submit Ticket
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'complaints' && (
                                    <div>
                                        <h2 className="h4 font-weight-bold text-dark border-bottom pb-2 mb-4">File a Complaint</h2>

                                        <div className="row mb-5">
                                            <div className="col-md-12">
                                                <div className="p-3 border rounded bg-white">
                                                    <h3 className="h6 font-weight-bold text-dark mb-3">Register a New Issue</h3>
                                                    {complaintSuccessMessage && (
                                                        <div className="alert alert-success small py-2 mb-3">{complaintSuccessMessage}</div>
                                                    )}
                                                    <form onSubmit={handleComplaintSubmit}>
                                                        <div className="row mb-2">
                                                            <div className="col-sm-6 mb-2 mb-sm-0">
                                                                <label className="text-dark small font-weight-bold">Select Active Booking</label>
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    value={selectedBookingForComplaint}
                                                                    onChange={(e) => setSelectedBookingForComplaint(e.target.value)}
                                                                >
                                                                    <option value="">N/A - General Issue</option>
                                                                    {bookings.map(b => (
                                                                        <option key={b.id} value={b.id}>{b.vehicleBrand} {b.vehicleName} ({b.id})</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div className="col-sm-6">
                                                                <label className="text-dark small font-weight-bold">Issue Category</label>
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    value={complaintCategory}
                                                                    onChange={(e) => setComplaintCategory(e.target.value)}
                                                                >
                                                                    <option>Vehicle issue</option>
                                                                    <option>Late delivery</option>
                                                                    <option>Convenience fee dispute</option>
                                                                    <option>Other</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className="form-group mb-3">
                                                            <label className="text-dark small font-weight-bold">Detailed Description</label>
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                required
                                                                rows={4}
                                                                value={complaintDesc}
                                                                onChange={(e) => setComplaintDesc(e.target.value)}
                                                                placeholder="Please explain the issue in detail..."
                                                            />
                                                        </div>
                                                        <button type="submit" className="btn btn-sm btn-primary px-3" style={{ borderRadius: '20px' }}>
                                                            Register Complaint
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="h6 font-weight-bold text-dark mb-3">Complaint Log History</h3>
                                            {complaints.length === 0 ? (
                                                <p className="text-muted small font-italic">No complaints registered.</p>
                                            ) : (
                                                <div className="list-group">
                                                    {complaints.map(c => (
                                                        <div className="list-group-item list-group-item-action p-3 mb-2 border rounded" key={c.id}>
                                                            <div className="d-flex w-100 justify-content-between align-items-center mb-1 flex-wrap">
                                                                <h5 className="h6 font-weight-bold text-dark mb-0">{c.category} ({c.id})</h5>
                                                                <span className="badge badge-warning text-dark px-2 py-1" style={{ borderRadius: '15px', fontSize: '10px' }}>{c.status}</span>
                                                            </div>
                                                            <p className="mb-2 text-muted small">{c.description}</p>
                                                            <small className="text-muted d-block">Booking Ref: {c.bookingId} | Filed on: {moment(c.dateFiled).format('LL')}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'refunds' && (
                                    <div>
                                        <h2 className="h4 font-weight-bold text-dark border-bottom pb-2 mb-4">Refunds Overview & Timelines</h2>

                                        <div className="row mb-4">
                                            <div className="col-md-6 mb-3">
                                                <div className="p-3 border rounded bg-white h-100">
                                                    <h3 className="h6 font-weight-bold text-primary mb-2">💸 Security Deposit Refunds</h3>
                                                    <p className="small text-muted mb-0" style={{ lineHeight: '1.5' }}>
                                                        All security deposit refunds are initiated automatically once the vehicle returns to our hub and passes a check-in inspection. The credit will reflect in your account within <strong>24 to 48 hours</strong>.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="p-3 border rounded bg-white h-100">
                                                    <h3 className="h6 font-weight-bold text-primary mb-2">❌ Cancellation & Booking Refunds</h3>
                                                    <p className="small text-muted mb-0" style={{ lineHeight: '1.5' }}>
                                                        Cancel bookings up to 24 hours prior to pick-up time for a <strong>100% refund</strong>. Cancellations within 24 hours incur a 50% rental fee deduction.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="h6 font-weight-bold text-dark mb-3">Refund Statuses</h3>
                                            {bookings.filter(b => b.status === 'Refund Requested' || b.status === 'Refunded').length === 0 ? (
                                                <p className="text-muted small font-italic">No refund requests active.</p>
                                            ) : (
                                                <div className="list-group">
                                                    {bookings.filter(b => b.status === 'Refund Requested' || b.status === 'Refunded').map(b => (
                                                        <div className="list-group-item p-3 mb-2 border rounded bg-white" key={`refund-${b.id}`}>
                                                            <div className="d-flex w-100 justify-content-between align-items-center flex-wrap mb-1">
                                                                <strong className="text-dark small">{b.vehicleBrand} {b.vehicleName} ({b.id})</strong>
                                                                <span className="badge badge-info text-white px-2 py-1" style={{ borderRadius: '15px', fontSize: '10px' }}>{b.status}</span>
                                                            </div>
                                                            <span className="text-muted small d-block">Refund Amount: ₹{b.amount}/- | Processing to original UPI account.</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Multiple Sub-Payments UPI Scanner Modal */}
            {showPaymentModal && (
                <div className="modal-backdrop-blur" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(5px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '15px'
                }}>
                    <div className="card border-0 shadow-lg p-4" style={{ borderRadius: '15px', maxWidth: '450px', width: '100%', backgroundColor: '#fff' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="h5 font-weight-bold text-dark mb-0">Initiate Booking Payment</h3>
                            <button
                                type="button"
                                className="close text-dark"
                                onClick={() => setShowPaymentModal(false)}
                                style={{ fontSize: '24px', border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>
                        <p className="text-muted small mb-3">
                            Enter the payment type and amount below, scan the code, and confirm with your 12-digit transaction ID.
                        </p>
                        <form onSubmit={handleNewPaymentSubmit}>
                            <div className="form-group mb-2">
                                <label className="text-dark small font-weight-bold mb-1">Payment Type</label>
                                <select
                                    className="form-control form-control-sm text-dark"
                                    value={paymentModalForm.type}
                                    onChange={(e) => setPaymentModalForm({ ...paymentModalForm, type: e.target.value })}
                                >
                                    <option>Remaining Balance</option>
                                    <option>Security Deposit</option>
                                    <option>Extension Charge</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div className="form-group mb-3">
                                <label className="text-dark small font-weight-bold mb-1">Enter Amount (₹)</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm text-dark"
                                    required
                                    min="1"
                                    placeholder="Enter payment amount"
                                    value={paymentModalForm.amount}
                                    onChange={(e) => setPaymentModalForm({ ...paymentModalForm, amount: e.target.value })}
                                />
                            </div>

                            {Number(paymentModalForm.amount) > 0 && (
                                <div className="text-center mb-3">
                                    <div className="d-inline-block p-2 rounded bg-white border mb-2">
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(getSubPaymentUpiUri())}`}
                                            alt="UPI QR Code"
                                            style={{ width: '150px', height: '150px' }}
                                        />
                                    </div>
                                    <div className="small text-muted mb-2">
                                        Amount: <strong>₹{paymentModalForm.amount}/-</strong><br />
                                        Payee: <code>crewtechsolutions@upi</code>
                                    </div>
                                </div>
                            )}

                            <div className="form-group mb-3">
                                <label className="text-dark small font-weight-bold mb-1">UPI Transaction ID / Ref No.</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm text-dark"
                                    required
                                    pattern="[0-9]{12}"
                                    title="Please enter a valid 12-digit UPI transaction reference number"
                                    placeholder="12-digit transaction ID"
                                    value={paymentModalForm.transactionId}
                                    onChange={(e) => setPaymentModalForm({ ...paymentModalForm, transactionId: e.target.value })}
                                />
                            </div>

                            <button
                                type="submit"
                                className="btn btn-success btn-block py-2 font-weight-bold"
                                style={{
                                    borderRadius: '30px',
                                    fontSize: '13px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    cursor: (paymentModalForm.transactionId.length !== 12 || !/^[0-9]{12}$/.test(paymentModalForm.transactionId)) ? 'not-allowed' : 'pointer'
                                }}
                                disabled={paymentModalForm.transactionId.length !== 12 || !/^[0-9]{12}$/.test(paymentModalForm.transactionId)}
                            >
                                Confirm Payment via WhatsApp
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default MyAccount;
